import { Link, NavLink, useLocation } from 'react-router';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { mainNavItems, adminNavItems } from '@/config/navigation';
import { useAuth } from '@/features/auth/hooks';
import { Brand } from '@/components/shared/brand';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export function Sidebar() {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navItems = isAdmin ? [...mainNavItems, ...adminNavItems] : mainNavItems;
  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 shrink-0 items-center border-b border-sidebar-border px-5 lg:h-18">
        <Brand to="/dashboard" />
      </div>
      <nav aria-label="Main navigation" className="flex-1 space-y-1 p-4">
        <p className="px-3 pb-3 pt-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Play & discover
        </p>
        {navItems.map((item) => {
          const active =
            location.pathname === item.href ||
            (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
          return (
            <NavLink
              key={item.href}
              to={item.href}
              onClick={() => setMobileOpen(false)}
              className={cn(
                'flex min-h-11 items-center gap-3 rounded-md border border-transparent px-3 py-2 text-sm font-medium transition-colors duration-150',
                active
                  ? 'border-sidebar-primary/30 bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )}
            >
              <item.icon
                className={cn('size-5 shrink-0', active && 'text-sidebar-primary')}
                aria-hidden="true"
              />
              <span>{item.title}</span>
              {active && (
                <span
                  className="ml-auto size-1.5 rounded-full bg-sidebar-primary"
                  aria-hidden="true"
                />
              )}
            </NavLink>
          );
        })}
      </nav>
      <div className="mx-4 mb-4 rounded-lg border bg-card p-4">
        <p className="text-sm font-medium">A game of conviction.</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Practice with virtual coins. Build your edge.
        </p>
        <Link
          to="/markets"
          onClick={() => setMobileOpen(false)}
          className="mt-3 inline-flex min-h-9 items-center gap-2 text-sm font-medium text-primary"
        >
          Explore markets
          <ArrowUpRight className="size-4" />
        </Link>
      </div>
      {user && (
        <div className="border-t border-sidebar-border p-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-semibold text-accent-foreground">
              {user.name.charAt(0).toUpperCase()}
            </span>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <>
      <DialogPrimitive.Root open={mobileOpen} onOpenChange={setMobileOpen}>
        <DialogPrimitive.Trigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="fixed left-3 top-3 z-40 lg:hidden"
            aria-label="Open navigation"
          >
            <Menu />
          </Button>
        </DialogPrimitive.Trigger>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-overlay lg:hidden" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed inset-y-0 left-0 z-50 w-sidebar overflow-y-auto border-r bg-sidebar text-sidebar-foreground shadow-lg lg:hidden"
          >
            <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <Button
                size="icon"
                variant="ghost"
                className="absolute right-2 top-3"
                aria-label="Close navigation"
              >
                <X />
              </Button>
            </DialogPrimitive.Close>
            {sidebarContent}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <aside className="sticky top-0 hidden h-dvh w-sidebar shrink-0 overflow-y-auto border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:block">
        {sidebarContent}
      </aside>
    </>
  );
}
